import { useCallback, useEffect, useState } from 'react';
import * as sched from 'sched';
import { useAuth } from './useAuth';

// ---------------------------------------------------------------------------
// Cross-consumer sync
// ---------------------------------------------------------------------------
//
// A detail page may toggle "Watch" while the dashboard panel is mounted in a
// sibling tab. Each consumer owns its own state but shares a tiny event bus
// so mutations propagate immediately without round-tripping through the
// network. Events carry the workspace_id they affect.

type Listener = (workspaceId: string) => void;
const listeners = new Set<Listener>();

function broadcast(workspaceId: string) {
  listeners.forEach((l) => l(workspaceId));
}

/** URL segment for the resource type, matching the backend route layout. */
function resourceSegment(target: sched.WatchTarget): string {
  switch (target.type) {
    case 'task':
      return 'tasks';
    case 'outcome':
      return 'outcomes';
    case 'team':
      return 'teams';
  }
}

// ---------------------------------------------------------------------------
// HTTP helper
// ---------------------------------------------------------------------------

async function apiFetch<T>(
  url: string,
  token: string,
  options?: RequestInit,
): Promise<T> {
  const resp = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options?.headers ?? {}),
    },
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`API ${resp.status}: ${text}`);
  }
  if (resp.status === 204 || resp.status === 202) {
    const text = await resp.text().catch(() => '');
    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  }
  return resp.json();
}

/** Like `apiFetch`, but returns `null` for 404 instead of throwing. Used by
 *  `useWatchTarget` to distinguish "not watching" (404) from a real error. */
async function apiFetchOrNull<T>(
  url: string,
  token: string,
  options?: RequestInit,
): Promise<T | null> {
  const resp = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options?.headers ?? {}),
    },
  });
  if (resp.status === 404) return null;
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`API ${resp.status}: ${text}`);
  }
  return resp.json();
}

// ---------------------------------------------------------------------------
// useWatchlist — list every watch the caller holds in this workspace
// ---------------------------------------------------------------------------

export function useWatchlist(workspaceId: string | undefined) {
  const { token } = useAuth();
  const apiUrl = process.env.API_URL ?? '';

  const [watches, setWatches] = useState<sched.Watch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!token || !workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<sched.ListWatchesResponse>(
        `${apiUrl}/api/workspaces/${workspaceId}/viewer/watches`,
        token,
      );
      setWatches(data.watches);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token, workspaceId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!workspaceId) return;
    const listener: Listener = (ws) => {
      if (ws === workspaceId) refresh();
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, [workspaceId, refresh]);

  const removeWatch = useCallback(
    async (target: sched.WatchTarget) => {
      if (!token || !workspaceId) return;
      const segment = resourceSegment(target);
      await apiFetch<void>(
        `${apiUrl}/api/workspaces/${workspaceId}/${segment}/${target.id}/watches/viewer`,
        token,
        { method: 'DELETE' },
      );
      broadcast(workspaceId);
    },
    [apiUrl, token, workspaceId],
  );

  return { watches, loading, error, refresh, removeWatch };
}

// ---------------------------------------------------------------------------
// useWatchTarget — does the caller watch a specific target?
//
// Used by the WatchButton on detail pages so we don't pay for a full-list
// fetch on every detail-page mount.
// ---------------------------------------------------------------------------

export function useWatchTarget(
  workspaceId: string | undefined,
  target: sched.WatchTarget,
) {
  const { token } = useAuth();
  const apiUrl = process.env.API_URL ?? '';

  const [watching, setWatching] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stable URL — recompute only when identity bits change.
  const url = workspaceId
    ? `${apiUrl}/api/workspaces/${workspaceId}/${resourceSegment(target)}/${target.id}/watches/viewer`
    : null;

  const refresh = useCallback(async () => {
    if (!token || !url) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetchOrNull<sched.Watch>(url, token);
      setWatching(data !== null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, url]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Sync with mutations from sibling consumers (dashboard remove, etc).
  useEffect(() => {
    if (!workspaceId) return;
    const listener: Listener = (ws) => {
      if (ws === workspaceId) refresh();
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, [workspaceId, refresh]);

  const startWatching = useCallback(async () => {
    if (!token || !url || !workspaceId) return;
    const body: sched.UpdateWatchRequest = { config: {} };
    await apiFetch<sched.Watch>(url, token, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    setWatching(true);
    broadcast(workspaceId);
  }, [token, url, workspaceId]);

  const stopWatching = useCallback(async () => {
    if (!token || !url || !workspaceId) return;
    await apiFetch<void>(url, token, { method: 'DELETE' });
    setWatching(false);
    broadcast(workspaceId);
  }, [token, url, workspaceId]);

  return { watching, loading, error, refresh, startWatching, stopWatching };
}
